import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatDuration, formatPlays, getCategoryName } from "@/utils/video";

const heroImages: Record<string, string> = {
  v1: "https://readdy.ai/api/search-image?query=Wide%20cinematic%20concert%20hall%20with%20warm%20orange%20spotlights%2C%20silhouettes%20of%20performers%20on%20stage%2C%20cheering%20crowd%2C%20dramatic%20bokeh%20lights%2C%20energetic%20atmosphere%2C%20professional%20event%20photography%2C%20high%20detail&width=1600&height=900&seq=td-hero-v1&orientation=landscape",
  v7: "https://readdy.ai/api/search-image?query=Close%20up%20of%20an%20adorable%20fluffy%20cat%20rubbing%20against%20an%20owner%20hand%20in%20a%20bright%20cozy%20living%20room%2C%20warm%20natural%20light%2C%20soft%20bokeh%20background%2C%20cute%20pet%20lifestyle%20photography%2C%20cheerful%20mood&width=1600&height=900&seq=td-hero-v7&orientation=landscape",
  v10: "https://readdy.ai/api/search-image?query=Vibrant%20night%20market%20food%20street%20with%20glowing%20warm%20lanterns%2C%20sizzling%20street%20food%20stalls%2C%20steam%20and%20evening%20lights%2C%20lively%20local%20atmosphere%2C%20cinematic%20food%20documentary%20photography&width=1600&height=900&seq=td-hero-v10&orientation=landscape",
  v16: "https://readdy.ai/api/search-image?query=Sleek%20modern%20tech%20gadgets%20arranged%20on%20a%20clean%20minimal%20desk%2C%20soft%20studio%20lighting%2C%20subtle%20warm%20accents%2C%20futuristic%20minimal%20aesthetic%2C%20high%20detail%20product%20photography&width=1600&height=900&seq=td-hero-v16&orientation=landscape",
  v20: "https://readdy.ai/api/search-image?query=Massive%20concert%20crowd%20at%20night%20raising%20hands%20with%20confetti%20and%20powerful%20stage%20lights%2C%20silhouettes%2C%20electric%20live%20music%20atmosphere%2C%20cinematic%20wide%20shot&width=1600&height=900&seq=td-hero-v20&orientation=landscape",
};

export default function HeroCarousel({ videos }: { videos: Video[] }) {
  const [index, setIndex] = useState(0);
  const count = videos.length;

  const go = useCallback(
    (dir: number) => {
      setIndex((prev) => (prev + dir + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % count), 6000);
    return () => clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const current = videos[index];
  const image = heroImages[current.id] ?? current.cover;

  return (
    <section className="relative w-full">
      <div className="relative h-[420px] md:h-[560px] w-full overflow-hidden">
        {videos.map((video, i) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={heroImages[video.id] ?? video.cover}
              alt={video.title}
              title={`${video.title} 糖豆视频`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/45 to-foreground-950/30"></div>

        <div className="relative z-10 h-full w-full px-4 md:px-8 flex flex-col justify-end pb-12 md:pb-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label font-medium">
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-fire-fill"></i>
              </span>
              精选推荐
            </span>
            <h2 className="mt-4 text-2xl md:text-4xl font-heading font-bold text-background-50 leading-tight">
              {current.title}
            </h2>
            <p className="mt-3 text-sm md:text-base text-background-100/90 line-clamp-2 max-w-xl">
              {current.description}
            </p>
            <div className="mt-3 flex items-center flex-wrap gap-x-4 gap-y-1 text-xs md:text-sm text-background-100/80">
              <span>{current.author}</span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-play-circle-line"></i>
                </span>
                {formatPlays(current.plays)} 次播放
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </span>
                {formatDuration(current.duration)}
              </span>
              <Link
                to={`/category/${current.category}`}
                className="px-2 py-0.5 rounded-full bg-background-50/20 hover:bg-background-50/30 transition-colors"
              >
                {getCategoryName(current.category)}
              </Link>
            </div>
            <Link
              to={`/detail/${current.id}`}
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-fill"></i>
              </span>
              立即观看
            </Link>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一个"
          onClick={() => go(-1)}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/25 text-background-50 hover:bg-background-50/40 transition-colors backdrop-blur cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一个"
          onClick={() => go(1)}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/25 text-background-50 hover:bg-background-50/40 transition-colors backdrop-blur cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 right-4 md:right-8 z-20 flex items-center gap-2">
          {videos.map((video, i) => (
            <button
              key={video.id}
              type="button"
              aria-label={`第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-7 bg-primary-400" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}